import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';

const { FiCalendar, FiPlus, FiTrash2, FiClock, FiAlertCircle } = FiIcons;

export default function AvailabilityCalendar({ utrederId }) {
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSlot, setNewSlot] = useState({
    start_date: '',
    end_date: '',
    type: 'unavailable',
    notes: ''
  });

  useEffect(() => {
    fetchAvailability();
  }, [utrederId]);

  const fetchAvailability = async () => {
    try {
      const { data, error } = await supabase
        .from('utreder_availability_1788615000000')
        .select('*')
        .eq('utreder_id', utrederId)
        .order('start_date', { ascending: true });

      if (error) throw error;
      setAvailability(data || []);
    } catch (err) {
      console.error('Error fetching availability:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('utreder_availability_1788615000000')
        .insert([{ ...newSlot, utreder_id: utrederId }]);

      if (error) throw error;
      setShowAdd(false);
      setNewSlot({ start_date: '', end_date: '', type: 'unavailable', notes: '' });
      fetchAvailability();
    } catch (err) {
      alert('Kunne ikke lagre tilgjengelighet: ' + err.message);
    }
  };

  const handleDeleteSlot = async (id) => {
    if (!window.confirm('Vil du slette denne perioden?')) return;
    try {
      const { error } = await supabase
        .from('utreder_availability_1788615000000')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchAvailability();
    } catch (err) {
      console.error('Error deleting slot:', err);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiCalendar} className="text-blue-600" />
          <h3 className="font-semibold text-slate-800">Min Tilgjengelighet</h3>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} />
          Marker utilgjengelig
        </button>
      </div>

      <div className="p-4">
        {showAdd && (
          <form onSubmit={handleAddSlot} className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Fra dato</label>
                <input
                  type="date"
                  required
                  value={newSlot.start_date}
                  onChange={(e) => setNewSlot({ ...newSlot, start_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Til dato</label>
                <input
                  type="date"
                  required
                  value={newSlot.end_date}
                  onChange={(e) => setNewSlot({ ...newSlot, end_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Årsak / Notat</label>
              <input
                type="text"
                placeholder="F.eks. Ferie eller fullbooket"
                value={newSlot.notes}
                onChange={(e) => setNewSlot({ ...newSlot, notes: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded-lg"
              >
                Avbryt
              </button>
              <button
                type="submit"
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Lagre periode
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="py-8 text-center text-slate-400 text-sm">Laster kalender...</div>
        ) : availability.length === 0 ? (
          <div className="py-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <SafeIcon icon={FiClock} className="mx-auto text-slate-300 text-2xl mb-2" />
            <p className="text-slate-500 text-sm">Ingen utilgjengelige perioder markert.</p>
            <p className="text-slate-400 text-xs mt-1">Du er for øyeblikket tilgjengelig for alle nye oppdrag.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {availability.map((slot) => (
              <div 
                key={slot.id}
                className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:border-blue-300 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600">
                    <SafeIcon icon={FiAlertCircle} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-800">
                      {new Date(slot.start_date).toLocaleDateString('no-NO')} - {new Date(slot.end_date).toLocaleDateString('no-NO')}
                    </div>
                    <div className="text-xs text-slate-500">{slot.notes || 'Utilgjengelig'}</div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteSlot(slot.id)}
                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}